const $ = window.$
import React from 'react'
import ButtonLoader from 'js/studios/shared/button_loader'
import {connect} from 'react-redux'
import {loginFacebook, sendRequest, GoogleLoginButton, googleLoginAvailable, AppleSigninButton, appleSigninAvailable} from 'js/studios/shared/reg/auth'
import {parseResponseError} from 'js/studios/shared/http'
import {SvgIcon} from 'js/studios/shared/svg_icons'
import {trackEventSP} from 'js/studios/shared/tracking'
import {TIMELINE_FALLBACK_LEAD_COEFFICIENT, TIMELINE_CHOICES, SHIPPING_COUNTRY_CHOICES, RECIPIENT_CHOICES, INTENT_CHOICES,
        CATEGORY_COEFFICIENTS, CATEGORY_FALLBACK_COEFFICIENT} from './data'
import {SHOULDER_DESIGNS, SIGNET_SHOULDER_DESIGNS_FALLBACK_LEAD_COEFFICIENT} from './signet'
import {PreviousLink} from './shared'
import {keyActivationHandler, LinkFormattedText} from 'js/studios/shared/utils'
import * as Sentry from "@sentry/react"

function isMobile(){
  return window.screen.width < 480
}

function isFacebookAppBrowser() {
  return !navigator?.userAgent || navigator.userAgent.includes("FB_IAB/")
}

function isEngagement(state){
  return state.projectCreation.flow === 'engagement' || state.projectCreationData.categoryTop === 'engagement'
}

function isSignet(state){
  return state.projectCreationData.categorySecondary === "signet_ring" || state.projectCreationData.sourceCategory === 'signet_ring'
}

function isNecklace(state){
  return state.projectCreationData.categorySecondary === "necklace" || state.projectCreationData.sourceCategory === 'necklace'
}

function isClassRing(state){
  return state.projectCreationData.categorySecondary === "class_ring" || state.projectCreationData.sourceCategory === 'class_ring'
}

function generateLeadValue(state, attachments){
  let budgetCoeff = 1, mobileCoeff, timelineCoeff = 1, recipientIntentCoeff = 1, shippingCountryCoeff = 1,
      shoulderDesignCoeff = 1, detailsLengthCoeff = 1, attachmentsCoeff = 1, categoryCoeff = 1

  if (isEngagement(state)){
    // maximum budget
    const budgetMax = state.projectCreationData.budgetMax
    if (!budgetMax){
      budgetCoeff = 25
    } else {
      let budgetMaxNumeric = parseInt(budgetMax.replace("+", ""))
      if (budgetMax === '24000+'){
        budgetCoeff = 10
      } else if (budgetMaxNumeric < 1000){
        budgetCoeff = 15
      } else if (budgetMaxNumeric < 1500){
        budgetCoeff = 18
      } else if (budgetMaxNumeric < 2500){
        budgetCoeff = 33
      } else if (budgetMaxNumeric < 15000){
        budgetCoeff = 45
      } else if (budgetMaxNumeric <= 24000){
        budgetCoeff = 70
      }
    }

    // recipient and purchase intent, combined
    const recipient = state.projectCreationData.recipient
    const intent = state.projectCreationData.intent
    const selectedRecipientChoice = RECIPIENT_CHOICES.find(c => c.value === recipient)
    let recipientCoeff = 1
    if (selectedRecipientChoice) {
      recipientCoeff = selectedRecipientChoice.lead_coefficient
    }
    const selectedIntentChoice = INTENT_CHOICES.find(c => c.value === intent)
    let intentCoeff = 1
    if (selectedIntentChoice) {
      intentCoeff = selectedIntentChoice.lead_coefficient
    }
    recipientIntentCoeff = recipientCoeff * intentCoeff

    // "when do you need this"
    const timeline = state.projectCreationData.timeline
    const selectedTimelineChoice = TIMELINE_CHOICES.find(c => c.value === timeline)
    if (selectedTimelineChoice) {
      timelineCoeff = selectedTimelineChoice.lead_coefficient
    } else {
      timelineCoeff = TIMELINE_FALLBACK_LEAD_COEFFICIENT
    }
  } else {
    // base value per category
    const category = state.projectCreationData.categorySecondary
    if (category in CATEGORY_COEFFICIENTS){
      categoryCoeff = CATEGORY_COEFFICIENTS[category]
    } else {
      categoryCoeff = CATEGORY_FALLBACK_COEFFICIENT
    }

    // length of input in the details field
    if (typeof state.projectCreationData.details  === 'string'){
      const detailsLength = state.projectCreationData.details.length
      if (detailsLength < 10) {
        detailsLengthCoeff = isSignet(state) ? 0.4 : 0.1
      } else if (detailsLength < 50) {
        detailsLengthCoeff = isSignet(state) ? 0.5 : 0.4
      } else if (detailsLength < 100) {
        detailsLengthCoeff = isSignet(state) ? 0.9 : 0.6
      } else if (detailsLength < 200) {
        detailsLengthCoeff = 1.3
      } else {
        detailsLengthCoeff = isSignet(state) ? 2.6 : 2.7
      }
    }

    // were any attachments uploaded to the details form?
    if (attachments.length > 0){
      attachmentsCoeff = isSignet(state) ? 1.3 : 1.4
    } else {
      attachmentsCoeff = isSignet(state) ? 0.7 : 0.6
    }

    if (isSignet(state)){
      // include designs on the signet ring shoulders?
      const shoulderDesigns = state.projectCreationData.shoulderDesigns
      const selectedShoulderDesignChoice = SHOULDER_DESIGNS.find(c => c.name === shoulderDesigns)
      if (selectedShoulderDesignChoice) {
        shoulderDesignCoeff = selectedShoulderDesignChoice.lead_coefficient
      } else {
        shoulderDesignCoeff = SIGNET_SHOULDER_DESIGNS_FALLBACK_LEAD_COEFFICIENT
      }
    }
  }

  // shipping destination country
  // (not applying shipping country coefficient to engagement ring projects for now)
  const shippingCountry = state.projectCreationData.shippingLocation
  const selectedShippingCountryChoice = SHIPPING_COUNTRY_CHOICES.find(c => c.value === shippingCountry)
  if (selectedShippingCountryChoice) {
    if (isEngagement(state)){
      shippingCountryCoeff = selectedShippingCountryChoice.engagement_lead_coefficient
    } else if (isSignet(state)){
      shippingCountryCoeff = selectedShippingCountryChoice.signet_lead_coefficient
    } else {
      shippingCountryCoeff = selectedShippingCountryChoice.other_lead_coefficient
    }
  } else {
    shippingCountryCoeff = 1
  }


  // mobile device (based on device screen size)?
  if (isMobile()){
    if (isEngagement(state)){
      mobileCoeff = 0.9
    } else if(isSignet(state)){
      mobileCoeff = 0.8
    } else{
      mobileCoeff = 0.8
    }
  } else {
    if (isEngagement(state)){
      mobileCoeff = 1.7
    } else if(isSignet(state)){
      mobileCoeff = 1.5
    } else{
      mobileCoeff = 1.4
    }
  }

  let spamCoef = 1.0
  if (isFacebookAppBrowser()) {
    spamCoef = 0.2
  }

  return (budgetCoeff * mobileCoeff * timelineCoeff * recipientIntentCoeff *
      shippingCountryCoeff * shoulderDesignCoeff * detailsLengthCoeff *
      attachmentsCoeff * categoryCoeff * spamCoef)
}

function submitProject(){
  const state = window.cm.store.getState()
  const projectCreation = state.projectCreation
  const projectCreationData = state.projectCreationData
  const data = $.extend(true, {}, projectCreationData)
  data.flow = projectCreation.flow

  data.attachments = state.uploadsWidget.uploads.filter(upload => upload.uploaderKey === "share-your-idea" && (!upload.error || upload.error.length === "")).map(upload => upload.id)

  data.formType = 'other'
  if (isEngagement(state)){
    data.formType = 'engagement'
  } else if (isSignet(state)){
    data.formType = 'signet'
  } else if (isNecklace(state)){
    data.formType = 'pendant'
  } else if (isClassRing(state)){
    data.formType = 'class_ring'
  }

  data.legacyLeadValue = generateLeadValue(state, data.attachments)

  return new Promise((resolve, reject) => {
    $.ajax({
      type: "POST",
      url: window.cm.urls['create-project-api'],
      data: JSON.stringify(data),
      success: (resp) => {
        resolve()
        window.location.href = resp.url
      },
      error: (resp) => {
        const error = parseResponseError(resp)
        trackEventSP('Project submitted', 'Create a project', {
          'error_message': error,
          'post_id': null
        })

        Sentry.withScope(function(scope) {
          scope.setExtras({ error: error, data: data, status: resp.status })
          Sentry.captureMessage(new Error('Could not submit post'))
        })

        reject(error)
      }
    })
  })
}

function regTrackSubmit(name, success, reason_for_fail, email){
  trackEventSP(name, 'Customer sign up', {
    'success': success,
    'reason_for_fail': reason_for_fail,
    'email_address': email
  })
}


class FBButton extends React.Component {
  render(){
    return <ButtonLoader className="btn btn-primary fb-button" loading={this.props.loading} onClick={this.props.onClick}>
      <SvgIcon name="icon-facebook" color="#fff" size="20px" />
      Continue with Facebook
    </ButtonLoader>
  }
}

class LoggedIn extends React.Component {

  constructor(props){
    super(props)
    this.state = {
      loading: false,
      error: ""
    }
  }

  submit(e){
    e.preventDefault()
    if (!this.state.loading){
      this.setState({
        loading: true
      })
      submitProject().catch((errorMessage) => {
        this.setState({
          loading: false,
          error: errorMessage
        })
      })
    }
  }

  componentDidMount(){
    trackEventSP('Sign up or log in modal displayed', 'Customer sign up', {
      'source': 'post-creation',
      'panel': 'already-logged-in'
    })
  }

  render(){
    return (
      <form className="frame-wrapper signup fade-in" onSubmit={this.submit.bind(this)}>
        <div className="frame-contents-wrapper">
          <div className="frame">
            <h2><PreviousLink />Let's Get Started!</h2>
            <p>You are now ready to start your design experience.</p>
            {this.state.error.length > 0 && (
              <div className="form-general-error">{this.state.error}</div>
            )}
          </div>
        </div>
        <div className="action-bar">
          <ButtonLoader type="submit" className="btn btn-primary btn-block btn-lg" loading={this.state.loading} disabled={this.state.loading}>Let's go!</ButtonLoader>
        </div>
      </form>
    )
  }
}

class ChooseMethod extends React.Component {

  constructor(props){
    super(props)
    this.state = {
      fbLoading: false,
      error: ""
    }
  }

  signInFacebook(e){
    e.preventDefault()
    if (this.state.loading || this.state.fbLoading){
      return false
    }
    this.setState({
      fbLoading: true
    })

    loginFacebook()
      .then(function(resp){
        regTrackSubmit('Customer Facebook sign up submitted', true, '', resp.email)
        submitProject().catch((errorMessage) => {
          this.setState({
            fbLoading: false,
            error: errorMessage
          })
        })
      }.bind(this))
      .catch(function(errorMessage){
        regTrackSubmit('Customer Facebook sign up submitted', false, errorMessage, '')
        this.setState({
          fbLoading: false,
          error: errorMessage
        })
      }.bind(this))
  }

  signInGoogle(result) {
    if (result.error) {
      regTrackSubmit('Customer Google sign up submitted', false, result.error, '')
      this.setState({ error: result.error })
    } else {
      regTrackSubmit('Customer Google sign up submitted', true, '', result.email)
      return submitProject().catch((errMsg) => this.setState({ error: errMsg }))
    }
  }

  signInApple(result) {
    if (result.error) {
      regTrackSubmit('Customer Apple sign up submitted', false, result.error, '')
      this.setState({ error: result.error })
    } else {
      regTrackSubmit('Customer Apple sign up submitted', true, '', result.email)
      return submitProject().catch((errMsg) => this.setState({ error: errMsg }))
    }
  }

  componentDidMount(){
    trackEventSP('Sign up or log in modal displayed', 'Customer sign up', {
      'source': 'post-creation',
      'panel': 'registration'
    })
  }


  render(){
    return (
      <div className="fade-in">
        <div className="btn-set">
          <a className={"btn btn-primary btn-block btn-lg" + (this.state.fbLoading ? " disabled" : "")} tabIndex="0" onClick={this.props.switchToSignUp} onKeyDown={keyActivationHandler(this.props.switchToSignUp)}>Continue with Email</a>
          <div className="or-separator">or</div>
          {googleLoginAvailable() && <GoogleLoginButton handler={this.signInGoogle.bind(this)} signup={true} />}
          {appleSigninAvailable() && <AppleSigninButton handler={this.signInApple.bind(this)} signup={true} />}
          <FBButton loading={this.state.fbLoading} onClick={this.signInFacebook.bind(this)} />
        </div>
        <p className="field">
          Already have an account? <a tabIndex="0" onClick={this.props.switchToSignIn} onKeyDown={keyActivationHandler(this.props.switchToSignIn)}>Sign in</a>
        </p>
      </div>
    )
  }
}

@connect(state => ({user: state.user}))
class EmailSignUp extends React.Component {

  constructor(props){
    super(props)
    this.state = {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      passwordConfirm: "",
      loading: false,
      error: ""
    }
  }

  isValid(){
    if (this.state.password !== this.state.passwordConfirm){
      this.setState({
        error: "Passwords do not match. Please try again."
      })
      return false
    } else if (this.state.firstName.length === 0 || this.state.lastName.length === 0 || this.state.email.length === 0 || this.state.password.length === 0){
      this.setState({
        error: "Please fill out all fields before continuing."
      })
      return false
    }
    return true
  }

  signUp(e){
    e.preventDefault()
    if (!this.state.loading && this.isValid()){
      this.setState({
        error: "",
        loading: true
      })


      // If registration was successful but not project creation, sometimes we
      // can have a logged in user submitting...
      if (this.props.user.id){
        submitProject().catch((errorMessage) => {
          this.setState({
            loading: false,
            error: errorMessage
          })
        })
      } else {
        let data = {
          email: this.state.email,
          password: this.state.password,
          firstName: this.state.firstName,
          lastName: this.state.lastName,
          agreement: true
        }
        sendRequest('signupFull', data)
          .then(function(resp){
            regTrackSubmit('Customer email sign up submitted', true, '', resp.email)
            submitProject().catch((errorMessage) => {
              this.setState({
                loading: false,
                error: errorMessage
              })
            })
          }.bind(this))
          .catch(function(errorMessage){
            regTrackSubmit('Customer email sign up submitted', false, errorMessage,  data.email)
            this.setState({
              loading: false,
              error: errorMessage
            })
          }.bind(this))
        }
    }
  }

  updateField(field, e){
    let state = {}
    state[field] = $(e.target).val()
    this.setState(state)
  }

  componentDidMount(){
    trackEventSP('Sign up or log in modal displayed', 'Customer sign up', {
      'source': 'post-creation',
      'panel': 'email-registration'
    })
  }

  render(){
    return (
      <form className="fade-in" onSubmit={this.signUp.bind(this)}>
        <div className="field">
          <input type="text" aria-label="First Name" placeholder="First Name" onChange={this.updateField.bind(this, 'firstName')} value={this.state.firstName} />
        </div>
        <div className="field">
          <input type="text" aria-label="Last Name" placeholder="Last Name" onChange={this.updateField.bind(this, 'lastName')} value={this.state.lastName} />
        </div>
        <div className="field">
          <input type="text" aria-label="Email" placeholder="Email" onChange={this.updateField.bind(this, 'email')} value={this.state.email} />
        </div>
        <div className="field">
          <input type="password" aria-label="Password" placeholder="Password" onChange={this.updateField.bind(this, 'password')} value={this.state.password} />
        </div>
        <div className="field">
          <input type="password" aria-label="Confirm password" placeholder="Confirm password" onChange={this.updateField.bind(this, 'passwordConfirm')} value={this.state.passwordConfirm} />
        </div>
        {this.state.error && <div className="form-general-error"><LinkFormattedText>{this.state.error}</LinkFormattedText></div>}
        <div className="action-bar">
          <ButtonLoader type="submit" className="btn btn-primary btn-block btn-lg" loading={this.state.loading} disabled={this.state.loading}>Sign Up</ButtonLoader>
        </div>
        <p className="field">
          Already have an account? <a tabIndex="0" onClick={this.props.switchToSignIn} onKeyDown={keyActivationHandler(this.props.switchToSignIn)}>Sign In</a>
        </p>
      </form>
    )
  }
}


class SignInForm extends React.Component {

  constructor(props){
    super(props)
    this.state = {
      email: "",
      password: "",
      loading: false,
      fbLoading: false,
      error: ""
    }
  }

  isValid(){
    return this.state.email.length > 0 && this.state.password.length > 0
  }

  signIn(e){
    e.preventDefault()
    if (!this.state.fbLoading && !this.state.loading && this.isValid()){
      this.setState({
        loading: true
      })
      let data = {email: this.state.email, password: this.state.password, remember: false}
      sendRequest('login', data)
        .then(function(resp){
          regTrackSubmit('Customer logs in', true, '', resp.email)
          submitProject().catch((errorMessage) => {
            this.setState({
              loading: false,
              error: errorMessage
            })
          })
        }.bind(this))
        .catch(function(errorMessage){
          regTrackSubmit('Customer logs in', false, errorMessage, data.email)
          this.setState({
            loading: false,
            error: errorMessage
          })
        }.bind(this))
    }
  }

  componentDidMount(){
    trackEventSP('Sign up or log in modal displayed', 'Customer sign up', {
      'source': 'post-creation',
      'panel': 'login'
    })
  }

  signInFacebook(e){
    e.preventDefault()
    if (this.state.loading || this.state.fbLoading){
      return false
    }
    this.setState({
      fbLoading: true
    })

    loginFacebook()
      .then(function(resp){
        regTrackSubmit('Customer Facebook sign up submitted', true, '', resp.email)
        submitProject().catch((errorMessage) => {
          this.setState({
            fbLoading: false,
            error: errorMessage
          })
        })
      }.bind(this))
      .catch(function(errorMessage){
        regTrackSubmit('Customer Facebook sign up submitted', false, errorMessage, '')
        this.setState({
          fbLoading: false,
          error: errorMessage
        })
      }.bind(this))
  }

  signInGoogle(result) {
    if (result.error) {
      regTrackSubmit('Customer Google sign up submitted', false, result.error, '')
      this.setState({ error: result.error })
    } else {
      regTrackSubmit('Customer Google sign up submitted', true, '', result.email)
      return submitProject().catch((errMsg) => this.setState({ error: errMsg }))
    }
  }

  signInApple(result) {
    if (result.error) {
      regTrackSubmit('Customer Apple sign up submitted', false, result.error, '')
      this.setState({ error: result.error })
    } else {
      regTrackSubmit('Customer Apple sign up submitted', true, '', result.email)
      return submitProject().catch((errMsg) => this.setState({ error: errMsg }))
    }
  }

  updateField(field, e){
    let state = {}
    state[field] = $(e.target).val()
    this.setState(state)
  }

  render(){
    return (
      <form className="frame-wrapper signup fade-in" onSubmit={this.signIn.bind(this)}>
        <div className="frame-contents-wrapper">
          <div className="frame">
            <h2><PreviousLink />Welcome Back!</h2>
            <p>Let's get started! You are now ready to start your design experience</p>
            <div className="field">
              <input type="text" placeholder="Email" onChange={this.updateField.bind(this, 'email')} value={this.state.email} />
            </div>
            <div className="field">
              <input type="password" placeholder="Password" onChange={this.updateField.bind(this, 'password')} value={this.state.password}  />
            </div>
            {this.state.error && <div className="form-general-error"><LinkFormattedText>{this.state.error}</LinkFormattedText></div>}
            <p className="field">
              Forgot your password? <a onClick={this.props.switchToResetPassword}>Reset it</a>
            </p>
            <div className="btn-set">
              <ButtonLoader className={"btn btn-primary btn-block btn-lg" + (this.state.fbLoading ? " disabled" : "")} disabled={this.state.fbLoading} type="submit" loading={this.state.loading}>Sign In</ButtonLoader>
              <div className="or-separator">or</div>
              {googleLoginAvailable() && <GoogleLoginButton key="btn" handler={this.signInGoogle.bind(this)} /> }
              {appleSigninAvailable() && <AppleSigninButton handler={this.signInApple.bind(this)} />}
              <FBButton loading={this.state.fbLoading} onClick={this.signInFacebook.bind(this)} />
            </div>
            <p className="field">
              Don't have an account yet? <a onClick={this.props.switchToSignUp}>Sign up</a>
            </p>
          </div>
        </div>
      </form>
    )
  }
}


class ForgotPassword extends React.Component {

  constructor(props){
    super(props)
    this.state = {
      email: "",
      emailSent: false,
      error: "",
      loading: false
    }
  }

  updateEmail(e){
    this.setState({
      email: $(e.target).val()
    })
  }

  isValid(){
    if (this.state.email.length === 0){
      this.setState({
        error: "Please provide an email address"
      })
      return false
    }
    return true
  }

  send(e){
    e.preventDefault()
    if (!this.state.loading && this.isValid()){
      this.setState({
        error: "",
        loading: true
      })
      let data = {email: this.state.email}
      sendRequest('resetPassword', data)
        .then(function(){
          regTrackSubmit('Customer resets password', true, '', data.email)
          this.setState({
            emailSent: true,
            loading: false
          })
        }.bind(this))
        .catch(function(errorMessage){
          regTrackSubmit('Customer resets password', false, errorMessage, data.email)
          this.setState({
            loading: false,
            error: errorMessage
          })
        }.bind(this))
    }

  }

  resend(e){
    e.preventDefault()
    this.setState({
      emailSent: false
    })
  }

  componentDidMount(){
    trackEventSP('Sign up or log in modal displayed', 'Customer sign up', {
      'source': 'post-creation',
      'panel': 'forgot-password'
    })
  }

  render(){
    return (
      <div className="frame-wrapper signup fade-in">
        <div className="frame-contents-wrapper">
          <div className="frame">
            <h2><PreviousLink />Reset Password</h2>
            {!this.state.emailSent && (
              <form className="fade-in" onSubmit={this.send.bind(this)}>
                <p>Enter your email address below and we'll send you a link to reset your password</p>
                <div className="field">
                  <input type="text" placeholder="Email" onChange={this.updateEmail.bind(this)} value={this.state.email} />
                </div>
                {this.state.error.length > 0 && (
                  <div className="form-general-error">{this.state.error}</div>
                )}
                <div className="action-bar no-absolute">
                  <ButtonLoader type="submit" className="btn btn-primary btn-block btn-lg" loading={this.state.loading}>Reset</ButtonLoader>
                </div>
                <p className="field">
                  <a onClick={this.props.switchToSignIn}>Back to Login</a>
                </p>
              </form>
            )}
            {this.state.emailSent && (
              <div className="fade-in">
                <p>We've sent an email to {this.state.email}. After you've reset your password, please return and sign in. <a onClick={this.resend.bind(this)}>Resend?</a></p>
                <div className="action-bar">
                  <a className="btn btn-primary btn-block btn-lg" onClick={this.props.switchToSignIn}>Return to Sign In</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
}


class SignIn extends React.Component {

  constructor(props){
    super(props)
    this.state = {
      frame: 'signin'
    }
  }

  switchToFrame(frame, e){
    e.preventDefault()
    this.setState({
      frame: frame
    })
  }

  render(){
    if (this.state.frame === 'signin'){
      return <SignInForm switchToResetPassword={this.switchToFrame.bind(this, 'reset-password')} switchToSignUp={this.props.switchToSignUp} />
    }
    return <ForgotPassword switchToSignIn={this.switchToFrame.bind(this, 'signin')} />
  }
}

class SignUp extends React.Component {

  constructor(props){
    super(props)
    this.state = {
      frame: 'choose'
    }
  }

  switchToSignUp(e){
    e.preventDefault()
    this.setState({
      frame: 'signup'
    })
  }

  render(){
    return (
      <div className="frame-wrapper signup fade-in">
        <div className="frame-contents-wrapper">
          <div className="frame">
            <h2><PreviousLink />Let's Get Started!</h2>
            <p>You are now ready to start your design experience.</p>
            {this.state.frame === 'choose' && (
              <ChooseMethod switchToSignUp={this.switchToSignUp.bind(this)} switchToSignIn={this.props.switchToSignIn} />
            )}
            {this.state.frame === 'signup' && (
              <EmailSignUp switchToSignIn={this.props.switchToSignIn} />
            )}
          </div>
        </div>
      </div>
    )
  }
}

@connect(state => ({user: state.user}))
export class Registration extends React.Component {

  constructor(props){
    super(props)
    this.state = {
      frame: props.user.id ? 'logged-in': 'signup'
    }
  }

  setFrame(frame, _e){
    this.setState({
      frame: frame
    })
  }

  render(){

    if (this.state.frame === 'logged-in'){
      return <LoggedIn />
    } else if (this.state.frame === 'signup'){
      return <SignUp switchToSignIn={this.setFrame.bind(this, 'signin')} />
    }

    return <SignIn switchToSignUp={this.setFrame.bind(this, 'signup')} />
  }

}
